import { CloseOutlined } from "@mui/icons-material";
import {
    Button,
    Card,
    CardContent,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    tableCellClasses,
} from "@mui/material";

import { styled } from "@material-ui/core";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import FirstPageRoundedIcon from "@mui/icons-material/FirstPageRounded";
import LastPageRoundedIcon from "@mui/icons-material/LastPageRounded";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ERoles, TransactionType } from "../../config";
import useAuth from "../../hooks/useAuth";
import { getAllPostpaidTransactionsByFilterThunk, getAllTransactionsByFilterThunk } from "../../redux/userManagement/userManagementThunk";
import { calculateTime } from "../../utils/calculateTime";
import TablePaginationShowPage from "../../utils/tablePagination";
import AlertMessageComponent from "../components/AlertMessageComponent";
import FetchingSpinner from "../components/FetchingSpinner";
import NoRecords from "../components/NoRecords";

dayjs.extend(utc);
// import { showSnackbar } from '../store/snackbarSlice';
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        fontSize: 14,
        fontWeight: 600,
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.common.white,
        padding: "10px !important",
        width: "20vw",
    },
    [`&.${tableCellClasses.body}`]: {
        padding: "10px !important",
        fontSize: 14,
    },
    [`&.${tableCellClasses.head.tr}`]: {
        backgroundColor: "#79E0EE",
        width: "45vw",
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(even)": {
        backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

export default function TransactionHistoryModel({ open, handleClose, currentData, roleToFetchTransactions }) {
    const state = useSelector(state => state.userManagementSlice);
    const dispatch = useDispatch();
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [errorResponse, setErrorResponse] = useState("");
    const { user } = useAuth();

    const [searchParams, setSearchParams] = useState({
        name: roleToFetchTransactions === ERoles.RESELLER_ADMIN ? currentData?.data?.resellerName : currentData?.data?.orgName,
        userRole: roleToFetchTransactions,
        fromDate: dayjs().startOf("month"),
        toDate: dayjs(),
    });
    const [fromDate, setFromDate] = useState(dayjs(searchParams.fromDate));
    const [toDate, setToDate] = useState(dayjs(searchParams.toDate));

    const handleChangePage = (event, newPage) => {
        setCurrentPage(newPage);
    };

    const handleChangeRowsPerPage = event => {
        setPageSize(parseInt(event.target.value));
        setCurrentPage(0);
    };

    const handleFromDateChange = date => {
        setFromDate(date);
        setSearchParams(prev => ({ ...prev, fromDate: date }));
    };

    const handleToDateChange = date => {
        setToDate(date);
        setSearchParams(prev => ({ ...prev, toDate: date }));
    };

    const onClickClear = () => {
        const today = dayjs();
        const firstDayOfMonth = today.startOf("month");

        setFromDate(firstDayOfMonth);
        setToDate(today);

        const updatedSearchParams = {
            name: roleToFetchTransactions === ERoles.RESELLER_ADMIN ? currentData?.data?.resellerName : currentData?.data?.orgName,
            userRole: roleToFetchTransactions,
            fromDate: firstDayOfMonth,
            toDate: today,
        };

        setSearchParams(updatedSearchParams);

        if (currentData?.billingPaymentType === "Postpaid") {
            dispatch(
                getAllPostpaidTransactionsByFilterThunk({
                    billingPaymentType: "Postpaid",
                    currentPage: currentPage + 1,
                    pageSize,
                    registryId: currentData?.registryId,
                    orgId: currentData?.orgId,
                    roleName: user?.roleName,
                    searchParams: updatedSearchParams,
                }),
            );
        } else {
            dispatch(
                getAllTransactionsByFilterThunk({
                    currentPage: currentPage + 1,
                    pageSize,
                    roleName: user?.roleName,
                    registryId: currentData?.registryId,
                    orgId: currentData?.orgId,
                    searchParams: updatedSearchParams,
                }),
            );
        }
    };

    const onSearch = () => {
        // const formattedFromDate = searchParams.fromDate ? dayjs(searchParams.fromDate).format("YYYY-MM-DD") : null;
        // const formattedToDate = searchParams.toDate ? dayjs(searchParams.toDate).format("YYYY-MM-DD") : null;

        const search = {
            name: roleToFetchTransactions === ERoles.RESELLER_ADMIN ? currentData?.data?.resellerName : currentData?.data?.orgName,
            userRole: roleToFetchTransactions,
            fromDate: searchParams.fromDate,
            toDate: searchParams.toDate,
        };
        if (currentData?.billingPaymentType === "Postpaid") {
            dispatch(
                getAllPostpaidTransactionsByFilterThunk({
                    billingPaymentType: "Postpaid",
                    currentPage: currentPage + 1,
                    pageSize,
                    registryId: currentData?.registryId,
                    orgId: currentData?.orgId,
                    roleName: user?.roleName,
                    searchParams: search,
                }),
            );
        } else {
            dispatch(
                getAllTransactionsByFilterThunk({
                    currentPage: currentPage + 1,
                    pageSize,
                    roleName: user?.roleName,
                    registryId: currentData?.registryId,
                    orgId: currentData?.orgId,
                    searchParams: search,
                }),
            );
        }
    };

    useEffect(() => {
        if (currentData?.billingPaymentType === "Postpaid") {
            dispatch(
                getAllPostpaidTransactionsByFilterThunk({
                    billingPaymentType: "Postpaid",
                    currentPage: currentPage + 1,
                    pageSize,
                    registryId: currentData?.registryId,
                    orgId: currentData?.orgId,
                    roleName: user?.roleName,
                    searchParams,
                }),
            );
        } else {
            dispatch(
                getAllTransactionsByFilterThunk({
                    currentPage: currentPage + 1,
                    pageSize,
                    roleName: user?.roleName,
                    registryId: currentData?.registryId,
                    orgId: currentData?.orgId,
                    searchParams,
                }),
            );
        }
    }, [currentPage, pageSize]);

    return (
        <Dialog open={open} onClose={handleClose} sx={{ padding: "1rem", width: "85%", marginLeft: "15%" }} maxWidth={"large"}>
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Grid container justifyContent="space-between" alignItems="center" sx={{ width: "100%" }}>
                    <Typography variant="h5">
                        <b>Transaction History for {currentData?.data?.orgName ? currentData?.data?.orgName : currentData?.data?.resellerName}</b>
                    </Typography>
                    <Grid
                        container
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                        }}
                    >
                        <Grid item>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="From Date"
                                    format="DD-MM-YYYY"
                                    value={fromDate}
                                    onChange={handleFromDateChange}
                                    renderInput={params => <TextField {...params} fullWidth />}
                                    disableFuture
                                    minDate={dayjs("2024-09-01")}
                                    maxDate={dayjs(searchParams.toDate)}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item ml={3}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="To Date"
                                    value={toDate}
                                    format="DD-MM-YYYY"
                                    onChange={handleToDateChange}
                                    renderInput={params => <TextField {...params} fullWidth />}
                                    disableFuture
                                    minDate={dayjs(searchParams.fromDate)}
                                    maxDate={dayjs()}
                                />
                            </LocalizationProvider>
                        </Grid>
                    </Grid>
                </Grid>

                <IconButton onClick={() => handleClose()}>
                    <CloseOutlined />
                </IconButton>
            </DialogTitle>
            <Grid display={"flex"} justifyContent={"flex-end"}>
                <Button variant="outlined" color="error" onClick={onClickClear}>
                    Clear
                </Button>
                <Button variant="contained" color="primary" onClick={onSearch} sx={{ ml: 2, mr: 4 }}>
                    Search
                </Button>
            </Grid>
            <Divider variant="middle" />
            <DialogContent>
                <Card className="border border-secondary-50 " sx={{ minWidth: "40vw", minHeight: "40vh" }}>
                    <CardContent sx={{ padding: 0 }}>
                        {currentData.billingPaymentType === "Postpaid" ? (
                            state.controls.isGetAllPostpaidTransactionsLoading ? (
                                <FetchingSpinner />
                            ) : state.postpaidTransactionsByPageSize && state.postpaidTransactionsByPageSize.length > 0 ? (
                                <TableContainer component={Paper} className="table-container" sx={{ maxHeight: "70vh" }}>
                                    <Table stickyHeader aria-label="simple table">
                                        <TableHead className="p-3 mb-2 row">
                                            <TableRow>
                                                <StyledTableCell className="tableHeaderFont" align="left" sx={{ width: "100px", maxWidth: "100px" }}>
                                                    Sr No
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="left" sx={{ width: "200px", maxWidth: "200px" }}>
                                                    Date and Time (IST)
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="left" sx={{ width: "200px", maxWidth: "200px" }}>
                                                    Previous Amount
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="left" sx={{ width: "200px", maxWidth: "200px" }}>
                                                    Updated Amount
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="left" sx={{ width: "200px", maxWidth: "200px" }}>
                                                    Difference
                                                </StyledTableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {state.postpaidTransactionsByPageSize.map((transaction, index) => (
                                                <StyledTableRow key={index}>
                                                    <StyledTableCell>{currentPage * pageSize + index + 1}</StyledTableCell>
                                                    <StyledTableCell>{calculateTime(transaction.updatedAt)}</StyledTableCell>
                                                    <StyledTableCell>{transaction?.previousDocument?.maxBalanceLimit}</StyledTableCell>
                                                    <StyledTableCell>{transaction?.updatedDocument?.maxBalanceLimit}</StyledTableCell>
                                                    <StyledTableCell>{transaction?.balanceDifference}</StyledTableCell>
                                                </StyledTableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <NoRecords />
                            )
                        ) : state.controls.isGetAllTransactionsLoading ? (
                            <FetchingSpinner />
                        ) : state.transactionsByPageSize && state.transactionsByPageSize.length > 0 ? (
                            <TableContainer component={Paper} className="table-container" sx={{ maxHeight: "70vh" }}>
                                <Table stickyHeader aria-label="simple table">
                                    <TableHead className="p-3 mb-2 row">
                                        <TableRow>
                                            <StyledTableCell className="tableHeaderFont" align="left" sx={{ width: "100px", maxWidth: "100px" }}>
                                                Sr No
                                            </StyledTableCell>
                                            <StyledTableCell className="tableHeaderFont" align="left" sx={{ width: "200px", maxWidth: "200px" }}>
                                                Date and Time (IST)
                                            </StyledTableCell>
                                            <StyledTableCell className="tableHeaderFont" align="left">
                                                Description
                                            </StyledTableCell>
                                            <StyledTableCell className="tableHeaderFont" align="left" sx={{ width: "200px", maxWidth: "200px" }}>
                                                Credit
                                            </StyledTableCell>
                                            <StyledTableCell className="tableHeaderFont" align="left" sx={{ width: "200px", maxWidth: "200px" }}>
                                                Debit
                                            </StyledTableCell>
                                            <StyledTableCell className="tableHeaderFont" align="left" sx={{ width: "200px", maxWidth: "200px" }}>
                                                Balance
                                            </StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {state.transactionsByPageSize.map((transaction, index) => (
                                            <StyledTableRow key={index}>
                                                <StyledTableCell className="tableContentFont" align="center">
                                                    {currentPage * pageSize + index + 1}
                                                </StyledTableCell>
                                                <StyledTableCell className="tableContentFont" align="left">
                                                    <b>{transaction?.createdAt && calculateTime(transaction.createdAt)}</b>
                                                </StyledTableCell>
                                                <StyledTableCell className="tableContentFont" align="left">
                                                    <Tooltip
                                                        title={<p style={{ fontSize: ".7rem" }}>{transaction?.description}</p>}
                                                        className="cursor-pointer"
                                                    >
                                                        {transaction?.description
                                                            ? transaction?.description.length > 30
                                                                ? `${transaction?.description.substr(0, 30)}...`
                                                                : transaction?.description
                                                            : "-"}
                                                    </Tooltip>
                                                </StyledTableCell>
                                                <StyledTableCell className="tableContentFont" align="left">
                                                    {transaction.transactionType === TransactionType.CREDIT && (
                                                        <Typography color="green">{transaction?.amount?.toFixed(2)}</Typography>
                                                    )}
                                                </StyledTableCell>
                                                <StyledTableCell className="tableContentFont" align="left">
                                                    {transaction.transactionType === TransactionType.DEBIT && (
                                                        <Typography color="error">{transaction?.amount?.toFixed(2)}</Typography>
                                                    )}
                                                </StyledTableCell>
                                                <StyledTableCell className="tableContentFont" align="left">
                                                    <Typography>{transaction?.balance?.toFixed(2)}</Typography>
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <NoRecords />
                        )}

                        {currentData?.billingPaymentType === "Postpaid" && state.totalPostpaidTransactionsCount > 0 ? (
                            <Grid
                                className="my-2"
                                style={{
                                    zIndex: "2",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    padding: 10,
                                }}
                            >
                                <Grid
                                    style={{
                                        marginTop: "15px",
                                    }}
                                >
                                    Total Transactions: {state.totalPostpaidTransactionsCount}
                                </Grid>

                                <TablePaginationShowPage
                                    rowsPerPageOptions={[5, 10, 25, 100]}
                                    colSpan={3}
                                    count={state.totalPostpaidTransactionsCount}
                                    rowsPerPage={pageSize}
                                    page={currentPage}
                                    slotProps={{
                                        select: {
                                            "aria-label": "Rows per page",
                                        },
                                        actions: {
                                            showFirstButton: true,
                                            showLastButton: true,
                                            slots: {
                                                firstPageIcon: FirstPageRoundedIcon,
                                                lastPageIcon: LastPageRoundedIcon,
                                                nextPageIcon: ChevronRightRoundedIcon,
                                                backPageIcon: ChevronLeftRoundedIcon,
                                            },
                                        },
                                    }}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                                <AlertMessageComponent
                                    message={errorResponse}
                                    fieldName={"ErrorMessage"}
                                    handleClose={() => setErrorResponse("")}
                                    show={Boolean(errorResponse)}
                                />
                            </Grid>
                        ) : (
                            state.totalTransactionsCount > 0 && (
                                <Grid
                                    className="my-2"
                                    style={{
                                        zIndex: "2",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        padding: 10,
                                    }}
                                >
                                    <Grid
                                        style={{
                                            marginTop: "15px",
                                        }}
                                    >
                                        Total Transactions: {state.totalTransactionsCount}
                                    </Grid>

                                    <TablePaginationShowPage
                                        rowsPerPageOptions={[5, 10, 25, 100]}
                                        colSpan={3}
                                        count={state.totalTransactionsCount}
                                        rowsPerPage={pageSize}
                                        page={currentPage}
                                        slotProps={{
                                            select: {
                                                "aria-label": "Rows per page",
                                            },
                                            actions: {
                                                showFirstButton: true,
                                                showLastButton: true,
                                                slots: {
                                                    firstPageIcon: FirstPageRoundedIcon,
                                                    lastPageIcon: LastPageRoundedIcon,
                                                    nextPageIcon: ChevronRightRoundedIcon,
                                                    backPageIcon: ChevronLeftRoundedIcon,
                                                },
                                            },
                                        }}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                    />
                                    <AlertMessageComponent
                                        message={errorResponse}
                                        fieldName={"ErrorMessage"}
                                        handleClose={() => setErrorResponse("")}
                                        show={Boolean(errorResponse)}
                                    />
                                </Grid>
                            )
                        )}
                    </CardContent>
                </Card>
            </DialogContent>
        </Dialog>
    );
}
